<template>
  <div class="customers-by-product">
    <v-card class="mb-5 rounded-lg elevation-2">
      <v-card-title class="py-4">
        <div>
          <h4 class="text-h5 font-weight-medium mb-1">Phân loại khách hàng theo sản phẩm</h4>
          <div class="text-body-2 grey--text">Theo dõi khách hàng đã mua từng sản phẩm trong khoảng thời gian</div>
        </div>
      </v-card-title>
    </v-card>

    <v-row class="mb-3">
      <v-col v-for="card in summaryCards" :key="card.label" cols="12" sm="6" md="3">
        <v-card outlined class="rounded-lg pa-3" :class="card.background">
          <div class="d-flex align-center">
            <v-icon size="30" :color="card.color" class="mr-3">{{ card.icon }}</v-icon>
            <div>
              <div class="text-overline" :class="`${card.color}--text`">{{ card.label }}</div>
              <div class="text-h6 font-weight-bold">{{ card.currency ? formatCurrency(card.value) : formatNumber(card.value) }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="rounded-lg elevation-3">
      <v-card-title class="py-4">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-text-field v-model="search" label="Tìm theo tên hoặc số điện thoại" prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details />
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              ref="productAutocomplete"
              v-model="filters.productIds"
              :items="products"
              item-text="name"
              item-value="id"
              label="Chọn một hoặc nhiều sản phẩm"
              prepend-inner-icon="mdi-package-variant"
              outlined dense clearable hide-details multiple chips deletable-chips
              :menu-props="{ maxHeight: 420, closeOnContentClick: false, contentClass: 'product-filter-menu' }"
              :loading="productsLoading"
            >
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-action>
                    <v-checkbox :input-value="attrs.inputValue" color="primary" @click.stop="on.click" />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.sku">{{ item.sku }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu v-model="startMenu" :close-on-content-click="false" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filters.startDate" label="Từ ngày" prepend-inner-icon="mdi-calendar" readonly clearable outlined dense hide-details v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="filters.startDate" @input="startMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu v-model="endMenu" :close-on-content-click="false" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filters.endDate" label="Đến ngày" prepend-inner-icon="mdi-calendar" readonly clearable outlined dense hide-details v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="filters.endDate" :min="filters.startDate" @input="endMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn block color="primary" class="text-none" @click="applyFilters"><v-icon left>mdi-filter</v-icon>Lọc dữ liệu</v-btn>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn block color="success" class="text-none" :loading="exporting" @click="exportExcel"><v-icon left>mdi-file-excel</v-icon>Xuất Excel</v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
        class="elevation-0"
      >
        <template v-slot:item.customer="{ item }">
          <div class="font-weight-medium">{{ item.customer_name || 'Khách hàng' }}</div>
          <div class="text-caption grey--text"><v-icon x-small>mdi-phone</v-icon> {{ item.phone || 'Chưa có SĐT' }}</div>
        </template>
        <template v-slot:item.product="{ item }">
          <div class="font-weight-medium">{{ item.product_name }}</div>
          <div v-if="item.sku" class="text-caption grey--text">Mã: {{ item.sku }}</div>
        </template>
        <template v-slot:item.total_quantity="{ item }">{{ formatNumber(item.total_quantity) }}</template>
        <template v-slot:item.paid_amount="{ item }"><span class="green--text text--darken-1 font-weight-medium">{{ formatCurrency(item.paid_amount) }}</span></template>
        <template v-slot:item.last_purchase_date="{ item }">{{ formatDate(item.last_purchase_date) }}</template>
        <template v-slot:no-data><div class="py-8 grey--text">Không có dữ liệu phù hợp</div></template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import reportModule from '@/api/report';
import productModule from '@/api/products';

const reportActions = reportModule.actions;
const productActions = productModule.actions;

export default {
  name: 'CustomersByProduct',
  data() {
    return {
      search: '',
      searchTimer: null,
      loading: false,
      exporting: false,
      productsLoading: false,
      products: [],
      startMenu: false,
      endMenu: false,
      filters: { startDate: '', endDate: '', productIds: [] },
      rows: [],
      totalItems: 0,
      summary: { customer_count: 0, product_count: 0, total_quantity: 0, paid_amount: 0 },
      options: { page: 1, itemsPerPage: 10 },
      headers: [
        { text: 'Khách hàng', value: 'customer', sortable: false },
        { text: 'Sản phẩm đã mua', value: 'product', sortable: false },
        { text: 'Số đơn', value: 'order_count', align: 'center', sortable: false },
        { text: 'Số lượng', value: 'total_quantity', align: 'right', sortable: false },
        { text: 'Khách đã trả', value: 'paid_amount', align: 'right', sortable: false },
        { text: 'Lần mua gần nhất', value: 'last_purchase_date', sortable: false },
      ],
    };
  },
  computed: {
    summaryCards() {
      return [
        { label: 'Khách hàng', value: this.summary.customer_count, icon: 'mdi-account-group', color: 'blue', background: 'blue lighten-5' },
        { label: 'Sản phẩm', value: this.summary.product_count, icon: 'mdi-package-variant', color: 'purple', background: 'purple lighten-5' },
        { label: 'Số lượng đã mua', value: this.summary.total_quantity, icon: 'mdi-cart', color: 'orange', background: 'orange lighten-5' },
        { label: 'Khách đã trả', value: this.summary.paid_amount, icon: 'mdi-cash-check', color: 'green', background: 'green lighten-5', currency: true },
      ];
    },
  },
  watch: {
    options: { deep: true, handler() { this.fetchData(); } },
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.options.page = 1; this.fetchData(); }, 500);
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleProductMenuOutsideClick, true);
    this.fetchProducts();
    this.fetchData();
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleProductMenuOutsideClick, true);
  },
  methods: {
    closeProductMenu() {
      if (this.$refs.productAutocomplete) {
        this.$refs.productAutocomplete.isMenuActive = false;
      }
    },
    handleProductMenuOutsideClick(event) {
      const autocomplete = this.$refs.productAutocomplete?.$el;
      const menu = event.target.closest?.('.product-filter-menu');
      if (!menu && autocomplete && !autocomplete.contains(event.target)) {
        this.closeProductMenu();
      }
    },
    params(extra = {}) {
      return {
        start_date: this.filters.startDate || undefined,
        end_date: this.filters.endDate || undefined,
        search: this.search || undefined,
        product_ids: this.filters.productIds.length ? this.filters.productIds : undefined,
        page: this.options.page,
        per_page: this.options.itemsPerPage,
        ...extra,
      };
    },
    applyFilters() {
      this.closeProductMenu();
      if (this.filters.startDate && this.filters.endDate && this.filters.startDate > this.filters.endDate) {
        this.$snackbar.warning('Ngày bắt đầu không được lớn hơn ngày kết thúc');
        return;
      }
      this.options.page = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await reportActions.getCustomersByProduct(this.params());
        this.rows = response.data.data || [];
        this.totalItems = Number(response.data.meta?.total || 0);
        this.summary = response.data.total || this.summary;
      } catch (error) {
        const message = error.response?.data?.message || 'Không thể tải dữ liệu khách hàng theo sản phẩm';
        this.$snackbar.error(message);
        console.error('Customers by product report error:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchProducts() {
      this.productsLoading = true;
      try {
        const response = await productActions.getProductsV2({ per_page: 1000 });
        this.products = response.data.data || [];
      } catch (error) {
        this.$snackbar.error('Không thể tải danh sách sản phẩm');
      } finally {
        this.productsLoading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const response = await reportActions.getCustomersByProduct(this.params({ page: 1, per_page: 100 }));
        let allRows = response.data.data || [];
        const lastPage = Number(response.data.meta?.last_page || 1);
        for (let page = 2; page <= lastPage; page += 1) {
          const next = await reportActions.getCustomersByProduct(this.params({ page, per_page: 100 }));
          allRows = allRows.concat(next.data.data || []);
        }
        const sheet = XLSX.utils.json_to_sheet(allRows.map((item, index) => ({
          STT: index + 1,
          'Tên khách hàng': item.customer_name,
          'Số điện thoại': item.phone,
          'Sản phẩm': item.product_name,
          'Mã sản phẩm': item.sku,
          'Số đơn': Number(item.order_count),
          'Số lượng': Number(item.total_quantity),
          'Khách đã trả': Number(item.paid_amount),
          'Lần mua gần nhất': item.last_purchase_date,
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, 'Khách hàng theo sản phẩm');
        XLSX.writeFile(workbook, `khach-hang-theo-san-pham-${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.$snackbar.success('Xuất Excel thành công');
      } catch (error) {
        this.$snackbar.error('Không thể xuất file Excel');
      } finally {
        this.exporting = false;
      }
    },
    formatNumber(value) { return Number(value || 0).toLocaleString('vi-VN'); },
    formatCurrency(value) { return `${Number(value || 0).toLocaleString('vi-VN')} đ`; },
    formatDate(value) { return value ? new Date(`${value}T00:00:00`).toLocaleDateString('vi-VN') : '—'; },
  },
};
</script>
